import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultThrowOnError } from "@/queries/errors";
import { keyWithPrincipal } from "@/queries/principal-key";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { useQuery } from "@tanstack/react-query";
import type {
    GetOrganizationLLMUsageResponse,
    LLMUsageSummary,
    DailyUsage,
} from "gitpod-next-api/gitpod/v1/ona_intelligence_pb";
import { Code, ConnectError } from "@connectrpc/connect";

export type PlainLLMUsageSummary = PlainMessage<LLMUsageSummary>;
export type PlainDailyUsage = PlainMessage<DailyUsage>;
export type PlainGetOrganizationLLMUsageResponse = PlainMessage<GetOrganizationLLMUsageResponse>;

export const onaIntelligenceQueryKey = {
    usage: () => keyWithPrincipal(["ona-intelligence", "usage"]),
    enabled: () => keyWithPrincipal(["ona-intelligence", "enabled"]),
};

export const useOrganizationLLMUsage = () => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: onaIntelligenceQueryKey.usage(),
        queryFn: async () => {
            const response = await api.onaIntelligenceService.getOrganizationLLMUsage({});
            return toPlainMessage(response);
        },
        throwOnError: defaultThrowOnError,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60,
    });
};

export const useIsOnaIntelligenceEnabled = () => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: onaIntelligenceQueryKey.enabled(),
        queryFn: async () => {
            try {
                await api.onaIntelligenceService.getOrganizationLLMUsage({});
                return true;
            } catch (error) {
                const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
                if (isNotFound) {
                    return false;
                }
                throw error;
            }
        },
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
    });
};
